"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SidebarBookingForm } from "@/components/shared/SidebarBookingForm";
import { Reveal } from "@/components/ui/Reveal";
import { restoreLegacyParagraphs } from "@/lib/content/legacy-html";
import { publicUrl } from "@/lib/content/path";
import { DERMATOLOGIST_PAGE_DATA } from "@/lib/site-data";
import type { LegacyAboutPageData, LegacyGalleryItem, LegacyVideoItem } from "@/lib/content/about-legacy";
import { AnimatedBookingSection } from "@/components/ui/AnimatedBookingSection";
import { Awards } from "@/components/home/Awards";
import { CtaBand } from "@/components/home/CtaBand";
import { Doctors } from "@/components/home/Doctors";
import { WhyDermalife } from "@/components/home/WhyDermalife";
import { GoogleReviews } from "@/components/home/GoogleReviews";
import { CelebrityTrust } from "@/components/home/CelebrityTrust";

type MediaArticle = {
  media: string;
  heading: string;
  title: string;
  logo: string;
  publication: string;
  description: string;
  link: string;
};

const mediaArticles: MediaArticle[] = [
  {
    media: "NDTV",
    heading: "Featured in The NDTV",
    title: "Salmon Sperm Skincare Is Having A Moment, But Does It Actually Work? Experts Weigh In",
    publication: "NDTV",
    logo: "/images/press/ndtv-logo.webp",
    description:
      'It began, as most beauty crazes now do, in glossy clinics and on celebrity skin. The so-called salmon sperm facial quickly earned a reputation for delivering glassy, luminous complexions. Soon, whispers of "fish DNA" and miracle glow treatments travelled from aesthetic clinics in Seoul to Instagram feeds, even in India.',
    link: "https://www.theweek.in/news/health/2026/01/01/trying-to-fix-hair-fall-in-2026-here-are-6-mistakes-that-you-should-stop-doing-right-now.html",
  },
  {
    media: "The Week",
    heading: "Featured in The Week",
    title: "Trying to fix hair fall in 2026? Here are 6 mistakes that you should stop doing right now",
    publication: "The Week",
    logo: "/images/press/the-week-logo.webp",
    description:
      "In this feature, Dr Gaurav Garg explained the most common mistakes people make while attempting to treat hair fall including the excessive scalp treatments, ignoring nutritional deficiencies and blindly following the viral remedies. He emphasized the importance of a clear medical diagnosis before starting any hair loss treatment.",
    link: "https://www.theweek.in/news/health/2026/01/01/trying-to-fix-hair-fall-in-2026-here-are-6-mistakes-that-you-should-stop-doing-right-now.html",
  },
  {
    media: "India TV",
    heading: "Featured in India TV",
    title: "Hair fall isn’t always bad news: Dermatologist explains when it’s completely normal",
    publication: "India TV",
    logo: "/images/press/india-tv-logo.webp",
    description:
      "Here Dr Garg clarifies the science of daily hair shedding and reassures readers that losing 50 to 100 strands a day is normal. He also explained that hairfall that is due to hormonal , nutritional and stress related issues require medical attention.",
    link: "https://www.indiatvnews.com/lifestyle/beauty/hair-fall-sign-of-renewal-2026-01-15-1026029",
  },
  {
    media: "BW Wellbeing World",
    heading: "Featured in BW Wellbeing World",
    title: "Digital Health Advice Falls Short For Hair And Skin Conditions",
    publication: "BW Wellbeing World",
    logo: "/images/press/bw-wellbeing-world-logo.webp",
    description:
      "Dr Gaurav Garg tells the risks of relying on AI generated and social media driven health advice. He emphasizes on the importance of personalized clinical evaluation and evidence based dermatological protocols.",
    link: "https://www.bwwellbeingworld.com/article/digital-health-advice-falls-short-for-hair-and-skin-conditions-586558",
  },
  {
    media: "Hindustan Times",
    heading: "Featured in Hindustan Times",
    title: "Dermatologist reveals why ‘losing 50–100 hairs daily is normal’; shares when you should be concerned",
    publication: "Hindustan Times",
    logo: "/images/press/hindustan-times-logo.webp",
    description:
      "In this article, Dr Garg addresses the myths surrounding seasonal hair shedding, telogen effluvium and stress induced hair loss.",
    link: "https://www.hindustantimes.com/lifestyle/health/dermatologist-reveals-why-losing-50-100-hairs-daily-is-normal-shares-when-you-should-be-concerned-101768830966269.html",
  },
  {
    media: "NDTV",
    heading: "Featured in NDTV",
    title: "Can A Detox Diet Make Your Skin Look Dull? We Asked Experts",
    publication: "NDTV",
    logo: "/images/press/ndtv-logo.webp",
    description:
      "Dr Gaurav Garg explains how extreme detox diets can negatively affect skin barrier health and hair quality. He emphasized balanced nutrition as a cornerstone of healthy scalp and skin maintenance.",
    link: "https://www.ndtv.com/lifestyle/can-a-detox-diet-make-your-skin-look-dull-we-asked-experts-10999507",
  },
];
function ColoredText({ text, baseColor = "text-brand-black" }: { text: string; baseColor?: string }) {
  const words = text.split(" ");
  if (words.length <= 1) {
    return <span className={baseColor}>{text}</span>;
  }
  let orangeWordCount = 1;
  if (words.length >= 6) {
    orangeWordCount = 3;
  } else if (words.length >= 4) {
    orangeWordCount = 2;
  }
  const firstPart = words.slice(0, words.length - orangeWordCount).join(" ");
  const lastPart = words.slice(words.length - orangeWordCount).join(" ");
  return (
    <>
      <span className={baseColor}>{firstPart} </span>
      <span className="text-brand-orange">{lastPart}</span>
    </>
  );
}

function HtmlBlock({ html, className = "" }: { html?: string | null; className?: string }) {
  if (!html) return null;
  return <div className={className} dangerouslySetInnerHTML={{ __html: rewritePublicLinks(restoreLegacyParagraphs(html)) }} />;
}

function rewritePublicLinks(html: string) {
  return html.replace(/\b(href|src)=(["'])(.*?)\2/gi, (match, attribute: string, quote: string, value: string) => {
    return `${attribute}=${quote}${publicUrl(value)}${quote}`;
  });
}

import { ClientHeroVideo } from "@/components/shared/ClientHeroVideo";

function PageHero({ data }: { data: LegacyAboutPageData }) {
  return (
    <section className="relative isolate flex min-h-[600px] items-center overflow-hidden bg-brand-black pt-32 pb-16 lg:min-h-[760px] lg:pt-44 lg:pb-20">
      <ClientHeroVideo />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-brand-black/88 via-brand-black/45 to-brand-black/10" />
      <div className="absolute inset-0 -z-10 w-full max-w-[900px] bg-gradient-to-r from-brand-black/75 via-brand-black/35 to-transparent" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-brand-black/45 via-transparent to-transparent" />
      <div className="absolute inset-0 -z-10 brand-cell-pattern opacity-[0.05]" />
      <div className="absolute -right-32 -top-32 h-[520px] w-[520px] rounded-full bg-brand-orange/15 blur-[110px]" />

      <div className="relative flex w-full items-center px-4 sm:px-6 md:px-12 xl:px-[170px]">
        <Reveal className="max-w-2xl min-w-0">
          <nav aria-label="Breadcrumb" className="mb-7 flex items-center gap-2 text-sm text-white/60">
            <Link href="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <span className="text-brand-peach">{data.title}</span>
          </nav>
          <p className="font-accent text-xs uppercase tracking-widest text-brand-peach">
            Dermalife Skin &amp; Hair Clinic
          </p>
          <h1 className="mt-4 text-3xl font-semibold leading-[1.1] sm:text-4xl lg:text-5xl xl:text-6xl break-words">
            <ColoredText text={data.title} baseColor="text-white" />
          </h1>
        </Reveal>
      </div>
    </section>
  );
}

function HorizontalRules() {
  return (
    <div className="mt-5 space-y-2">
      <hr className="border-2 border-brand-black" />
      <hr className="w-32 border-2 border-brand-orange" />
    </div>
  );
}

function LegacyContentShell({ 
  topContent, 
  bottomContent, 
  sidebar = true 
}: { 
  topContent?: React.ReactNode; 
  bottomContent?: React.ReactNode; 
  sidebar?: boolean 
}) {
  return (
    <>
      <section className="w-full bg-white px-4 py-10 sm:px-6 sm:py-14 md:px-12 xl:px-[170px] border-b border-brand-gray-light/40">
        <div className={`grid w-full grid-cols-1 gap-12 min-w-0 ${sidebar ? "lg:grid-cols-[minmax(0,1fr)_400px]" : ""}`}>
          <div className="min-w-0 flex flex-col">
            {topContent}
          </div>
          {sidebar && (
            <aside className="min-w-0">
              <div className="sticky top-32 space-y-8">
                <SidebarBookingForm />
              </div>
            </aside>
          )}
        </div>
      </section>
      {bottomContent && (
        <div className="w-full px-4 sm:px-6 md:px-12 xl:px-[170px] mt-12 lg:mt-16">
          <div className="legacy-procedure-content text-brand-gray min-w-0">
            {bottomContent}
          </div>
        </div>
      )}
    </>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-4 list-disc space-y-2 pl-5 text-brand-gray">
      {items.map((item, index) => (
        <li key={index} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: rewritePublicLinks(item) }} />
      ))}
    </ul>
  );
}

function Paragraphs({ items }: { items: string[] }) {
  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <p key={index} className="text-justify leading-relaxed text-brand-gray" dangerouslySetInnerHTML={{ __html: rewritePublicLinks(item) }} />
      ))}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-8 text-2xl font-semibold">
      {typeof children === "string" ? (
        <ColoredText text={children} baseColor="text-brand-black" />
      ) : (
        children
      )}
    </h3>
  );
}

function GalleryLightbox({
  image,
  alt,
  onClose,
}: {
  image: string;
  alt: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-brand-black/85 px-5 backdrop-blur-sm">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-5 top-5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand-black"
      >
        Close
      </button>
      <div className="relative h-[82vh] w-full max-w-6xl overflow-hidden rounded-3xl bg-brand-black">
        <Image src={image} alt={alt} fill unoptimized className="object-contain p-4" />
      </div>
    </div>
  );
}

function VideoLightbox({ videoId, onClose }: { videoId: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-brand-black/85 px-5 backdrop-blur-sm">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-5 top-5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand-black"
      >
        Close
      </button>
      <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-brand-black shadow-2xl">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title="Dermalife video"
          className="aspect-video w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

function GalleryGrid({
  title,
  items,
  sidebar = true,
  bannerImage,
}: {
  title: string;
  items: LegacyGalleryItem[];
  sidebar?: boolean;
  bannerImage?: string | null;
}) {
  const [activeImage, setActiveImage] = useState<LegacyGalleryItem | null>(null);

  return (
    <LegacyContentShell
      sidebar={sidebar}
      topContent={
        <>
          <Reveal>
            <h2 className="text-3xl font-semibold">
              <ColoredText text={title} baseColor="text-brand-black" />
            </h2>
            <HorizontalRules />
          </Reveal>
          {bannerImage && (
            <div className="mb-8 w-full rounded-2xl bg-brand-gray-light/40 flex items-center justify-center overflow-hidden max-h-[500px] mt-6">
              <Image src={bannerImage} alt={title} width={1200} height={800} unoptimized priority className="w-full h-auto max-h-[500px] object-contain" />
            </div>
          )}
        </>
      }
      bottomContent={
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 min-w-0">
            {items.map((item, index) => (
              <Reveal key={`${item.image}-${index}`} delay={Math.min(index * 0.03, 0.3)}>
                <button
                  type="button"
                  onClick={() => setActiveImage(item)}
                  className="relative block aspect-[4/3] w-full overflow-hidden rounded-xl bg-brand-gray-light shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <Image
                    src={item.image}
                    alt={item.altText || item.title || title}
                    fill
                    unoptimized
                    className="object-cover"
                  />
                </button>
              </Reveal>
            ))}
          </div>
          {activeImage && (
            <GalleryLightbox
              image={activeImage.image}
              alt={activeImage.altText || activeImage.title || title}
              onClose={() => setActiveImage(null)}
            />
          )}
        </>
      }
    />
  );
}

function AboutDoctorPage({ data }: { data: LegacyAboutPageData }) {
  return (
    <>
      <LegacyContentShell
        topContent={
          <>
            <Reveal>
              <h2 className="text-3xl font-semibold">
                <ColoredText text={data.home.title || ""} baseColor="text-brand-black" />
              </h2>
              <HorizontalRules />
            </Reveal>

            {data.bannerImage && (
              <div className="mt-8 mb-8 w-full rounded-2xl bg-brand-gray-light/40 flex items-center justify-center overflow-hidden max-h-[500px]">
                <Image src={data.bannerImage} alt={data.title || ""} width={1200} height={800} unoptimized priority className="w-full h-auto max-h-[500px] object-contain" />
              </div>
            )}
          </>
        }
        bottomContent={
          <>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_280px] min-w-0">
              <Reveal className="min-w-0">
                <h3 className="text-2xl font-semibold">
                  <ColoredText text={data.home.head1 || ""} baseColor="text-brand-black" />
                </h3>
                <HtmlBlock
                  html={data.home.credentialsHtml}
                  className="cms-content mt-5 min-w-0 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_li]:leading-relaxed"
                />
                <Link
                  href="/dermatologist-in-delhi"
                  data-lead-popup
                  className="mt-7 inline-flex rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-orange-deep hover:shadow-md"
                >
                  Book Free Consultation with Dr. Gaurav Garg →
                </Link>
              </Reveal>

              {data.home.doctorImage && (
                <Reveal delay={0.1} className="min-w-0">
                  <div className="overflow-hidden rounded-2xl border border-brand-gray-light bg-brand-gray-light shadow-sm">
                    <Image
                      src={data.home.doctorImage}
                      alt="Dermatologist in Delhi"
                      width={520}
                      height={640}
                      unoptimized
                      className="h-auto w-full object-cover"
                    />
                  </div>
                </Reveal>
              )}
            </div>

            <Reveal>
              <div className="mt-10">
                <SectionHeading>Awards &amp; Achievements</SectionHeading>
                <BulletList items={DERMATOLOGIST_PAGE_DATA.awardsAndAchievements} />

                <SectionHeading>Why You Should Consult With Dr. Gaurav Garg</SectionHeading>
                <Paragraphs
                  items={[
                    "Aside from all the mainstream achievements in the medical field, Dr. Gaurav Garg has managed to excel in several other fields. He believes in providing his patients with the best treatment options with optimal transparency and satisfying results.",
                    'Some of the other reasons why Dr. Gaurav Garg is the <a href="/dermatologist-in-delhi.php" target="_blank"><strong>best skin specialist in Delhi</strong></a> include:',
                  ]}
                />
                <BulletList items={DERMATOLOGIST_PAGE_DATA.whyConsult} />
                <Paragraphs
                  items={[
                    "These are just a few of the highlights of Dr. Garg’s career. For someone who is extremely dedicated to his work and his patients, he has been working towards making skin and hair treatments a lot more accessible to patients from across India. His holistic approach to treatment is what makes him different from all the other dermatologists that you will likely come across in India.",
                  ]}
                />

                <SectionHeading>Dr. Gaurav Garg&apos;s Specialization</SectionHeading>
                <p className="mt-4 font-semibold text-brand-black">The list of treatments that Dr. Gaurav Garg specializes in are:</p>
                <BulletList items={DERMATOLOGIST_PAGE_DATA.specializations} />

                <SectionHeading>Workshops &amp; Lectures</SectionHeading>
                <p className="mt-4 leading-relaxed text-brand-gray">Dr Gaurav Garg is academically active and has organized workshops and lectures in India and Abroad.</p>
                <BulletList items={DERMATOLOGIST_PAGE_DATA.workshops} />

                <SectionHeading>Paper &amp; Poster Presentations</SectionHeading>
                <BulletList items={DERMATOLOGIST_PAGE_DATA.papers} />

                <SectionHeading>Publications &amp; in Journals and Chapters in textbooks</SectionHeading>
                <BulletList items={DERMATOLOGIST_PAGE_DATA.publications} />
              </div>
            </Reveal>
          </>
        }
      />

      {data.galleries.length > 0 && (
        <section className="py-10 sm:py-20 bg-white overflow-hidden">
          <div className="w-full px-0 sm:px-[170px]">
            <Reveal className="text-center mb-12 px-6 sm:px-0">
              <span className="font-accent text-xs uppercase text-brand-orange-deep tracking-widest block mb-3 font-semibold">
                Verified Certification
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold">
                <ColoredText text="Dr. Gaurav Garg's Credentials" baseColor="text-brand-black" />
              </h2>
            </Reveal>

            {/* Horizontal Auto-Scrolling Marquee */}
            <div className="relative flex overflow-hidden group w-full mask-edges">
              <div className="flex gap-6 animate-marquee min-w-max pb-8">
                {[...data.galleries, ...data.galleries].map((item, idx) => (
                  <div
                    key={`${item.image}-${idx}`}
                    className="shrink-0 w-[260px] sm:w-[320px] aspect-[4/3] relative rounded-2xl overflow-hidden border border-brand-gray-light bg-brand-gray-light/30 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                  >
                    <Image
                      src={item.image}
                      alt={item.altText || `Certificate Dr Gaurav Garg ${idx + 1}`}
                      fill
                      unoptimized
                      className="object-contain p-3"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Interaction hint */}
            <p className="text-center text-xs text-brand-gray/40 font-accent uppercase tracking-widest flex items-center justify-center gap-2 mt-2 font-medium">
              <span className="w-8 h-[1px] bg-brand-gray/20" />
              <span className="hidden md:inline">Hover over certificates to pause</span>
              <span className="md:hidden inline">Hold certificates to view</span>
              <span className="w-8 h-[1px] bg-brand-gray/20" />
            </p>
          </div>
        </section>
      )}

    </>
  );
}

function WhyUsPage({ data }: { data: LegacyAboutPageData }) {
  return (
    <LegacyContentShell
      topContent={
        <>
          <Reveal>
            <h2 className="text-3xl font-semibold">
              <ColoredText text="Why Choose Us" baseColor="text-brand-black" />
            </h2>
            <HorizontalRules />
          </Reveal>
          {data.bannerImage && (
            <div className="mt-8 mb-8 w-full rounded-2xl bg-brand-gray-light/40 flex items-center justify-center overflow-hidden max-h-[500px]">
              <Image src={data.bannerImage} alt={data.title || ""} width={1200} height={800} unoptimized priority className="w-full h-auto object-contain" />
            </div>
          )}
        </>
      }
      bottomContent={
        <HtmlBlock
          html={`${data.home.whyUsHtml || ""}${data.home.whyUsExtraHtml || ""}`}
          className="cms-content text-brand-gray"
        />
      }
    />
  );
}

function DifferentPage({ data }: { data: LegacyAboutPageData }) {
  return (
    <LegacyContentShell
      topContent={
        <>
          <Reveal>
            <h2 className="text-3xl font-semibold">
              <ColoredText text="What Makes Us Different" baseColor="text-brand-black" />
            </h2>
            <HorizontalRules />
          </Reveal>
          {data.bannerImage && (
            <div className="mt-8 mb-8 w-full rounded-2xl bg-brand-gray-light/40 flex items-center justify-center overflow-hidden max-h-[500px]">
              <Image src={data.bannerImage} alt={data.title || ""} width={1200} height={800} unoptimized priority className="w-full h-auto object-contain" />
            </div>
          )}
        </>
      }
      bottomContent={
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_280px] min-w-0">
          <HtmlBlock html={data.home.differenceHtml} className="cms-content text-brand-gray min-w-0" />
          {data.home.differenceImage && (
            <Reveal delay={0.1} className="min-w-0">
              <div className="overflow-hidden rounded-2xl bg-brand-gray-light shadow-sm">
                <Image src={data.home.differenceImage} alt="Boosted Fue Technique For Hair Transplantation." width={520} height={420} unoptimized className="h-auto w-full object-cover" />
              </div>
            </Reveal>
          )}
        </div>
      }
    />
  );
}

function TestimonialsPage({ data }: { data: LegacyAboutPageData }) {
  const [activeVideo, setActiveVideo] = useState<LegacyVideoItem | null>(null);
  return (
    <section className="px-5 py-14 sm:px-8 lg:py-16">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <h2 className="text-3xl font-semibold text-brand-black">
            Our <span className="text-brand-orange">Results</span>
          </h2>
        </Reveal>
        {data.bannerImage && (
          <div className="mt-8 mb-10 overflow-hidden rounded-3xl bg-brand-gray-light">
            <Image src={data.bannerImage} alt={data.title || ""} width={1400} height={900} unoptimized priority className="aspect-[1920/938] w-full object-contain bg-brand-gray-light" />
          </div>
        )}
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 min-w-0">
          {data.videos.map((video, index) => (
            <Reveal key={`${video.videoUrl}-${index}`} delay={Math.min(index * 0.03, 0.3)}>
              <button
                type="button"
                onClick={() => setActiveVideo(video)}
                className="group relative block aspect-[4/3] w-full overflow-hidden rounded-2xl bg-brand-gray-light shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                {video.thumbnail && <Image src={video.thumbnail} alt="Dermalife hair transplant testimonials" fill unoptimized className="object-cover" />}
                <span className="absolute inset-0 flex items-center justify-center bg-brand-black/20 group-hover:bg-brand-black/40 transition-colors">
                  {/* Backdrop blur/shadow mask to hide thumbnail play button watermarks */}
                  <span className="absolute h-[76px] w-[76px] rounded-full bg-brand-black/50 blur-[2px] transition-transform group-hover:scale-110" />
                  <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg transition-transform group-hover:scale-110">
                    <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      {activeVideo && <VideoLightbox videoId={activeVideo.videoUrl} onClose={() => setActiveVideo(null)} />}
    </section>
  );
}

function MediaCoveragePage({ data: _data }: { data: LegacyAboutPageData }) {
  return (
    <>
      <section className="px-5 py-12 sm:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="text-3xl font-semibold text-brand-black mb-8">
              Media Coverage – <span className="text-brand-orange">Dr. Gaurav Garg</span>
            </h2>
            <div className="mb-10 space-y-4 text-brand-gray">
              <p>Dr Gaurav Garg is a trusted hair transplant surgeon and dermatologist known for his expert opinions. He is regularly featured in leading national publications where journalists seek his opinion and patient first guidance on hair loss, scalp care and cosmetic dermatology.</p>
              <p>Below are selected media features highlighting his contributions to public health awareness and advanced hair transplant techniques.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Replaced static banner image with sleek React component, full width */}
      <CelebrityTrust />

      {mediaArticles.map((article, index) => (
        <section key={`${article.heading}-${index}`} className="border-b border-brand-gray-light px-5 py-10 sm:px-8">
          <div className="mx-auto grid grid-cols-1 max-w-7xl items-center gap-8 md:grid-cols-[180px_minmax(0,1fr)] min-w-0">
            <Reveal className="min-w-0 mx-auto">
              <div className="relative mx-auto h-24 w-44">
                <Image src={article.logo} alt={article.media} fill unoptimized className="object-contain" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="min-w-0">
              <h3 className="text-2xl font-semibold">
                <ColoredText text={article.heading} baseColor="text-brand-black" />
              </h3>
              <h4 className="mt-3 text-lg font-normal text-brand-black">{article.title}</h4>
              <h5 className="mt-3 text-base text-brand-gray"><strong>Publication:</strong> {article.publication}</h5>
              <p className="mt-4 leading-relaxed text-brand-gray">{article.description}</p>
              <a className="mt-5 inline-flex rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-orange-deep hover:shadow-md" href={article.link} target="_blank" rel="nofollow noreferrer">
                Read Full Article
              </a>
            </Reveal>
          </div>
        </section>
      ))}

      <section className="px-5 py-12 sm:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h3 className="text-3xl font-semibold text-brand-black">
              Media &amp; Academic <span className="text-brand-orange">Contributions</span>
            </h3>
            <div className="mt-5 space-y-4 text-brand-gray">
              <p>Dr. Gaurav Garg’s media work reflects his genuine commitment to patient education and responsible, science-backed medical communication. He believes people deserve clear, honest information about hair and scalp health — not confusing trends or internet myths.</p>
              <p>His work consistently focuses on:</p>
              <ul className="list-disc space-y-2 pl-5">
                <li><b>Scientific diagnosis and treatment of hair loss</b></li>
                <li><b>Advanced high-density FUE hair transplant techniques</b></li>
                <li><b>Scalp health and donor area preservation</b></li>
                <li><b>Nutritional and hormonal causes of hair thinning</b></li>
                <li><b>Safe and ethical cosmetic dermatology practices</b></li>
              </ul>
              <p>Backed by years of surgical experience and advanced training in hair restoration, Dr. Garg is respected not only as a skilled clinician but also as a trusted public voice who makes complex medical topics easy to understand.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function renderBody(data: LegacyAboutPageData) {
  switch (data.pageKey) {
    case "about-doctor":
      return <AboutDoctorPage data={data} />;
    case "why-us":
      return <WhyUsPage data={data} />;
    case "different":
      return <DifferentPage data={data} />;
    case "testimonials":
      return <TestimonialsPage data={data} />;
    case "global-client":
      return <GalleryGrid title="Dermalife Clinic Tour" items={data.galleries} bannerImage={data.bannerImage} />;
    case "clinic-tour":
      return <GalleryGrid title="Dermalife Clinic Tour" items={data.galleries} bannerImage={data.bannerImage} />;
    case "awards":
      return <GalleryGrid title="Awards" items={data.galleries} bannerImage={data.bannerImage} />;
    case "media-coverage":
      return <MediaCoveragePage data={data} />;
  }
}

export function LegacyAboutPageClient({ data }: { data: LegacyAboutPageData }) {
  return (
    <>
      <Header />
      <main className="flex-1 bg-white">
        <PageHero data={data} />
        {renderBody(data)}
        <WhyDermalife />
        <GoogleReviews />
        <CtaBand />
        <Doctors />
        <Awards />
        {data.pageKey === "global-client" && (
          <AnimatedBookingSection buttonText="Get Free Hair Transplant consultation" />
        )}
      </main>
      <Footer />
    </>
  );
}

